<template>
  <section class="captcha-page">
    <div class="captcha-card">
      <div class="captcha-card__icon" aria-hidden="true">✓</div>
      <span class="captcha-card__eyebrow">Verificación de seguridad</span>
      <h1>Confirma que eres una persona</h1>
      <p class="captcha-card__intro">
        Desliza el control hacia la derecha hasta completar la imagen.
      </p>

      <div
        ref="captcha"
        class="captcha"
        :class="{ 'captcha--passed': passed, 'captcha--error': failed }"
        :style="captchaStyle"
      >
        <div class="captcha__status" v-if="passed">
          <span>✓</span>
          Verificación completada
        </div>
      </div>

      <div
        ref="track"
        class="captcha-slider"
        :class="{ 'captcha-slider--passed': passed, 'captcha-slider--error': failed }"
      >
        <div class="captcha-slider__progress" :style="{ width: `${handlePosition + handleWidth}px` }"></div>
        <span class="captcha-slider__label">
          {{ passed ? "Verificación completada" : "Desliza para completar" }}
        </span>
        <button
          type="button"
          class="captcha-slider__handle"
          :style="{ transform: `translateX(${handlePosition}px)` }"
          :disabled="passed"
          aria-label="Deslizar para completar la verificación"
          @pointerdown="startMove"
          @keydown.right.prevent="moveWithKeyboard(12)"
          @keydown.left.prevent="moveWithKeyboard(-12)"
          @keydown.enter.prevent="validatePosition"
          @keydown.space.prevent="validatePosition"
        >
          <span>{{ passed ? "✓" : "›" }}</span>
        </button>
      </div>

      <p class="captcha-card__hint" :class="{ 'captcha-card__hint--error': failed }" role="status">
        {{ feedbackMessage }}
      </p>

      <button v-if="failed" type="button" class="captcha-retry" @click="resetCaptcha(true)">
        Intentar de nuevo
      </button>
    </div>
  </section>
</template>

<script>
import image1 from '../assets/ApoyoFinanciero.jpg';
import image2 from '../assets/Credimpulso.jpg';
import image3 from '../assets/CreditoEstudiantil.jpg';
import image4 from '../assets/IdeaDeNegocio.jpg';

export default {
  name: 'PuzzleCaptcha',
  emits: ['complete'],
  data() {
    return {
      images: [image1, image2, image3, image4],
      image: '',
      dragging: false,
      passed: false,
      failed: false,
      startClientX: 0,
      startHandlePosition: 0,
      handlePosition: 0,
      handleWidth: 56,
      maxTravel: 0,
      puzzleStart: 0,
    };
  },
  computed: {
    captchaStyle() {
      const progress = this.maxTravel > 0 ? this.handlePosition / this.maxTravel : 0;
      const puzzlePosition = this.puzzleStart * (1 - progress);
      return {
        backgroundImage: `url(${this.image})`,
        '--puzzle-moved': `${puzzlePosition}px`,
      };
    },
    feedbackMessage() {
      if (this.passed) return 'Listo. Estamos preparando el formulario.';
      if (this.failed) return 'La pieza no llegó a su posición. Vuelve a intentarlo.';
      return 'También puedes usar las flechas del teclado y presionar Enter.';
    },
  },
  mounted() {
    this.selectRandomImage();
    this.$nextTick(this.updateMeasurements);
    window.addEventListener('resize', this.updateMeasurements);
  },
  beforeUnmount() {
    this.removePointerListeners();
    window.removeEventListener('resize', this.updateMeasurements);
  },
  methods: {
    selectRandomImage() {
      this.image = this.images[Math.floor(Math.random() * this.images.length)];
    },
    updateMeasurements() {
      if (!this.$refs.track || !this.$refs.captcha) return;
      this.handleWidth = 56;
      this.maxTravel = Math.max(0, this.$refs.track.clientWidth - this.handleWidth - 8);
      const puzzleWidth = Math.min(76, this.$refs.captcha.clientWidth * 0.2);
      this.puzzleStart = -(this.$refs.captcha.clientWidth - puzzleWidth * 2);
      this.handlePosition = Math.min(this.handlePosition, this.maxTravel);
    },
    startMove(event) {
      if (this.passed) return;
      event.preventDefault();
      this.failed = false;
      this.dragging = true;
      this.startClientX = event.clientX;
      this.startHandlePosition = this.handlePosition;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      window.addEventListener('pointermove', this.handleMove, { passive: false });
      window.addEventListener('pointerup', this.endMove, { once: true });
      window.addEventListener('pointercancel', this.endMove, { once: true });
    },
    handleMove(event) {
      if (!this.dragging) return;
      event.preventDefault();
      const delta = event.clientX - this.startClientX;
      this.handlePosition = Math.max(
        0,
        Math.min(this.maxTravel, this.startHandlePosition + delta)
      );
    },
    endMove() {
      if (!this.dragging) return;
      this.dragging = false;
      this.removePointerListeners();
      this.validatePosition();
    },
    moveWithKeyboard(step) {
      if (this.passed) return;
      this.failed = false;
      this.handlePosition = Math.max(0, Math.min(this.maxTravel, this.handlePosition + step));
    },
    validatePosition() {
      if (this.maxTravel > 0 && this.handlePosition >= this.maxTravel * 0.96) {
        this.handlePosition = this.maxTravel;
        this.passed = true;
        this.failed = false;
        window.setTimeout(() => this.$emit('complete', true), 650);
        return;
      }
      this.failed = true;
      window.setTimeout(() => {
        if (!this.passed) this.handlePosition = 0;
      }, 180);
    },
    resetCaptcha(changeImage = false) {
      this.dragging = false;
      this.passed = false;
      this.failed = false;
      this.handlePosition = 0;
      if (changeImage) this.selectRandomImage();
      this.removePointerListeners();
      this.$nextTick(this.updateMeasurements);
    },
    removePointerListeners() {
      window.removeEventListener('pointermove', this.handleMove);
      window.removeEventListener('pointerup', this.endMove);
      window.removeEventListener('pointercancel', this.endMove);
    },
  },
};
</script>

<style scoped>
.captcha-page {
  min-height: calc(100vh - 210px);
  padding: 42px 18px 56px;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at 15% 5%, rgba(47, 126, 214, .14), transparent 22rem),
    #f3f7fc;
}

.captcha-card {
  width: min(100%, 520px);
  padding: 30px;
  text-align: center;
  border: 1px solid #dbe6f3;
  border-radius: 22px;
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 22px 58px rgba(13, 48, 120, .14);
}

.captcha-card__icon {
  width: 46px;
  height: 46px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: white;
  font-size: 22px;
  font-weight: 800;
  background: linear-gradient(135deg, #0d397d, #2473c9);
}

.captcha-card__eyebrow {
  color: #2473c9;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .09em;
  text-transform: uppercase;
}

.captcha-card h1 {
  margin: 7px 0 8px;
  color: #17375f;
  font-size: clamp(24px, 5vw, 31px);
  line-height: 1.15;
}

.captcha-card__intro {
  margin: 0 auto 22px;
  color: #64748b;
  font-size: 14px;
}

.captcha {
  --puzzle-size: 76px;
  --puzzle-moved: -250px;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 14px;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.5);
}

.captcha::before,
.captcha::after {
  content: "";
  position: absolute;
  inset: 0;
  width: inherit;
  height: inherit;
  background-image: inherit;
  background-size: inherit;
  background-position: inherit;
  clip-path: inset(
    calc((100% - var(--puzzle-size)) / 2)
    var(--puzzle-size)
    calc((100% - var(--puzzle-size)) / 2)
    calc(100% - var(--puzzle-size) * 2)
    round 8px
  );
}

.captcha::before {
  filter: brightness(.32);
}

.captcha::after {
  transform: translateX(var(--puzzle-moved));
  filter: drop-shadow(0 4px 5px rgba(0,0,0,.45));
  transition: transform .08s linear;
}

.captcha--error {
  animation: captcha-shake .3s ease;
}

.captcha__status {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: white;
  font-weight: 750;
  background: rgba(8, 74, 72, .76);
  backdrop-filter: blur(3px);
}

.captcha__status span {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #08756f;
  background: white;
}

.captcha-slider {
  position: relative;
  height: 58px;
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid #ccd9e9;
  border-radius: 13px;
  background: #eef3f8;
}

.captcha-slider__progress {
  position: absolute;
  inset: 0 auto 0 0;
  width: 56px;
  background: linear-gradient(90deg, #dcecff, #c8e2ff);
}

.captcha-slider__label {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding-left: 48px;
  color: #58708d;
  font-size: 13px;
  font-weight: 650;
  pointer-events: none;
}

.captcha-slider__handle {
  position: absolute;
  z-index: 2;
  top: 4px;
  left: 4px;
  width: 56px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  color: white;
  font-size: 30px;
  background: linear-gradient(135deg, #0d397d, #2473c9);
  box-shadow: 0 5px 13px rgba(13, 57, 125, .28);
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.captcha-slider__handle:active {
  cursor: grabbing;
}

.captcha-slider__handle:focus-visible {
  outline: 3px solid rgba(36, 115, 201, .3);
  outline-offset: 2px;
}

.captcha-slider--passed {
  border-color: #2b9b8f;
  background: #e9f8f5;
}

.captcha-slider--passed .captcha-slider__progress,
.captcha-slider--passed .captcha-slider__handle {
  background: #16867d;
}

.captcha-card__hint {
  min-height: 18px;
  margin: 12px 0 0;
  color: #718096;
  font-size: 12px;
}

.captcha-card__hint--error {
  color: #c2413a;
}

.captcha-retry {
  margin-top: 8px;
  padding: 8px 14px;
  border: 0;
  color: #174981;
  font-weight: 700;
  background: transparent;
  cursor: pointer;
}

@keyframes captcha-shake {
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@media (max-width: 560px) {
  .captcha-page {
    min-height: calc(100vh - 175px);
    padding: 22px 12px 34px;
  }

  .captcha-card {
    padding: 22px 16px;
    border-radius: 17px;
  }

  .captcha {
    --puzzle-size: 58px;
  }

  .captcha-slider__label {
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .captcha::after {
    transition: none;
  }
}
</style>
